import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * The website's ambient-effect switch, read once per page load.
 *
 * Cached in localStorage so a returning visitor gets the right answer on first paint instead of a
 * flash of falling code that shouldn't be there. If the request fails we fall back to the last
 * known answer, and failing that, to nothing — silence is the safe default for an effect.
 */
const LS_KEY = "cx_site_effects";
let inflight = null;

const read = () => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "null"); } catch { return null; }
};

export const fetchSiteEffects = () => {
  if (!inflight) {
    inflight = api.get("/public/site-effects")
      .then((r) => {
        const v = r.data?.code_rain || null;
        if (v) { try { localStorage.setItem(LS_KEY, JSON.stringify(v)); } catch { /* private mode */ } }
        return v;
      })
      .catch(() => read());
  }
  return inflight;
};

/**
 * `useCodeRain("home", "light")` — is the field on for this band, and how loud should it be?
 * Pale and navy bands carry their own loudness: the alpha that reads as slick on navy is
 * invisible on white.
 */
export function useCodeRain(pageKey, variant = "light") {
  const [cfg, setCfg] = useState(read);

  useEffect(() => {
    let live = true;
    fetchSiteEffects().then((v) => { if (live && v) setCfg(v); });
    return () => { live = false; };
  }, []);

  if (!cfg) return { on: false, intensity: "whisper" };
  const level = variant === "dark"
    ? (cfg.navy || cfg.intensity || "showcase")
    : (cfg.pale || cfg.intensity || "whisper");
  return {
    on: !!cfg.enabled && cfg.pages?.[pageKey] !== false,
    intensity: level,
  };
}

export const invalidateSiteEffects = () => {
  inflight = null;
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
};
